/**
 * Value formatting and type detection utilities for audit logs.
 */

import plur from 'plur';

import { UUID_PATTERN, USER_AGENT_FIELD_NAMES, ALL_CAPS_WORDS } from './constants';
import { formatDateTime } from '@customerio/pluma-components/react/utils/formatted-date-time';
import { isEmptyString } from '@/utils/string';

/**
 * Check if a value is a nullable database field object.
 * These have the structure: { string: "...", valid: true/false } or { int64: ..., valid: true/false }
 *
 * @example
 * isNullableDbField({ string: "hello", valid: true }) // true
 * isNullableDbField({ int64: 123, valid: true }) // true
 * isNullableDbField({ name: "test" }) // false
 */
export const isNullableDbField = (value: unknown): value is { string?: string; int64?: number; valid: boolean } => {
  if (value == null || typeof value !== 'object' || Array.isArray(value)) {
    return false;
  }
  const object = value as Record<string, unknown>;
  // Must have 'valid' as a boolean and either 'string' or 'int64' field
  if (typeof object.valid !== 'boolean') {
    return false;
  }
  // Check for exactly the expected keys (valid + string/int64)
  const keys = Object.keys(object);
  if (keys.length !== 2) {
    return false;
  }
  return 'string' in object || 'int64' in object;
};

/**
 * Extract the value from a nullable database field.
 * Returns null if not valid, otherwise returns the string or int64 value.
 */
export const extractNullableDbValue = (value: {
  string?: string;
  int64?: number;
  valid: boolean;
}): string | number | null => {
  if (!value.valid) {
    return null;
  }
  if ('string' in value) {
    return value.string ?? null;
  }
  if ('int64' in value) {
    return value.int64 ?? null;
  }
  return null;
};

/**
 * Check if a string is valid JSON (object or array).
 *
 * @param value - The string to check
 * @returns true if the string is valid JSON and starts with { or [
 *
 * @example
 * isJSONString('{"key": "value"}') // true
 * isJSONString('[1, 2, 3]') // true
 * isJSONString('not json') // false
 */
export const isJSONString = (value: string): boolean => {
  try {
    JSON.parse(value);
    return value.trim().startsWith('{') || value.trim().startsWith('[');
  } catch {
    return false;
  }
};

/**
 * Check if a value is redacted
 * Returns true for values like "[REDACTED]", "REDACTED", "[REDACTED:...]", or strings starting with "REDACTED" (case-insensitive)
 */
export const isRedactedValue = (value: unknown): boolean => {
  if (typeof value === 'string') {
    const normalized = value.trim().toUpperCase();
    // Check for exact match or pattern like [REDACTED:...]
    if (normalized === '[REDACTED]' || normalized.startsWith('[REDACTED')) {
      return true;
    }
    // Check for values starting with REDACTED (without brackets)
    return normalized.startsWith('REDACTED');
  }
  return false;
};

/**
 * Determine if a value should use monospace font (UUIDs, URLs, User Agents)
 * Note: Numbers and ID fields no longer get monospace styling for cleaner display
 * @param value - The value to check
 * @param fieldName - The field name for context
 * @returns true if value should be displayed in monospace
 */
export const shouldUseMonospace = (value: unknown, fieldName?: string): boolean => {
  if (typeof value === 'string') {
    const string_ = value.toLowerCase();
    const field = (fieldName ?? '').toLowerCase();

    // Check if value is a redacted value (e.g., "[REDACTED:8ce48da3]")
    if (string_.startsWith('[redacted')) {
      return true;
    }

    // Check if field name suggests it's a User Agent
    const normalizedField = field.replace(/[_-]/g, '');
    if (USER_AGENT_FIELD_NAMES.has(normalizedField)) {
      return true;
    }

    // Check if value looks like a User Agent string (starts with Mozilla/ or similar)
    if (string_.startsWith('mozilla/') || string_.includes('(compatible;') || string_.includes('applewebkit/')) {
      return true;
    }

    // Check if value looks like a UUID
    if (UUID_PATTERN.test(string_)) {
      return true;
    }

    // Check if value looks like a URL
    if (string_.startsWith('http://') || string_.startsWith('https://')) {
      return true;
    }
  }
  return false;
};

/**
 * Helper to check if an attribute name should use Code component styling
 * Note: ID fields no longer get code styling for cleaner display
 */
export const shouldUseCodeStyle = (_attributeName: string): boolean => {
  return false;
};

/**
 * Format boolean values for display
 */
export const formatBooleanValue = (value: boolean): string => {
  return value ? 'yes' : 'no';
};

/**
 * Check if a value looks like an email address
 * @param value - The value to check
 * @returns true if value appears to be an email
 */
export const isEmailValue = (value: unknown): boolean => {
  if (typeof value !== 'string') return false;
  // Basic email pattern: something@something.something
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
};

/**
 * Check if a string value matches a known acronym (case-insensitive).
 * Returns the properly cased acronym if found, otherwise null.
 */
const getAcronymValue = (value: string): string | null => {
  const upperValue = value.toUpperCase();
  const match = ALL_CAPS_WORDS.find((acronym) => acronym.toUpperCase() === upperValue);
  return match ?? null;
};

const ISO_TIMESTAMP_PATTERN = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d+)?(Z|[+-]\d{2}:\d{2})?$/;

const isUnixTimestampNumber = (n: number): boolean => (n > 1e9 && n < 1e10) || (n > 1e12 && n < 1e13);

const formatStringValue = (value: string): string => {
  if (value.length === 0) return '—';
  if (isRedactedValue(value)) return '[REDACTED]';
  if (ISO_TIMESTAMP_PATTERN.test(value)) {
    const formatted = formatDateTime(value, 'medium_with_time');
    if (!isEmptyString(formatted)) return formatted;
  }
  if (isDurationString(value)) return formatDuration(value);
  return getAcronymValue(value) ?? value;
};

const formatNumberValue = (value: number): string =>
  isUnixTimestampNumber(value) ? formatDateTime(value, 'medium_with_time') : String(value);

/**
 * Format a value for display in the audit log details view.
 *
 * Transformations applied:
 * - null/undefined → "—" (em dash)
 * - empty string → "—"
 * - boolean → "yes" / "no"
 * - ISO timestamp strings → human-readable date
 * - Duration strings (e.g., "168h0m0s") → human-readable (e.g., "1 week")
 * - Permission objects → formatted permission string
 * - Acronym values (sso, api, etc.) → uppercase (SSO, API)
 * - Other values → string representation
 *
 * @param value - The raw value to format
 * @returns Formatted string for display
 *
 * @example
 * formatValue(null) // "—"
 * formatValue(true) // "yes"
 * formatValue('168h0m0s') // "1 week"
 * formatValue('2025-11-27T00:41:08Z') // "Nov 27, 2025, 12:41:08 AM"
 * formatValue('sso') // "SSO"
 */
export const formatValue = (value: unknown): string => {
  if (value == null) return '—';

  // Nullable database fields: { string: "...", valid: true/false }
  if (isNullableDbField(value)) {
    const extracted = extractNullableDbValue(value);
    return extracted == null ? '—' : formatValue(extracted);
  }

  if (typeof value === 'string') return formatStringValue(value);
  if (typeof value === 'number') return formatNumberValue(value);
  if (typeof value === 'boolean') return formatBooleanValue(value);
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
};

/**
 * Check if a string is a Go-style duration format (e.g., "168h0m0s")
 */
export const isDurationString = (value: string): boolean => {
  return /^(\d+[dhmsw])+$/.test(value);
};

// Largest-first so the totalSeconds reduction walks from weeks to seconds.
const DURATION_UNITS: { suffix: string; seconds: number; singular: string; plural: string }[] = [
  { suffix: 'w', seconds: 7 * 24 * 60 * 60, singular: 'week', plural: 'weeks' },
  { suffix: 'd', seconds: 24 * 60 * 60, singular: 'day', plural: 'days' },
  { suffix: 'h', seconds: 60 * 60, singular: 'hour', plural: 'hours' },
  { suffix: 'm', seconds: 60, singular: 'minute', plural: 'minutes' },
  { suffix: 's', seconds: 1, singular: 'second', plural: 'seconds' },
];

/**
 * Parse and format Go-style duration strings (e.g., "168h0m0s" -> "1 week")
 */
export const formatDuration = (value: string): string => {
  let matched = false;
  let totalSeconds = 0;
  for (const unit of DURATION_UNITS) {
    const match = value.match(new RegExp(`(\\d+)${unit.suffix}`));
    if (match != null) {
      matched = true;
      totalSeconds += Number.parseInt(match[1], 10) * unit.seconds;
    }
  }

  if (!matched) return value;
  if (totalSeconds === 0) return '0 seconds';

  const parts: string[] = [];
  let remaining = totalSeconds;
  for (const unit of DURATION_UNITS) {
    const count = Math.floor(remaining / unit.seconds);
    if (count > 0) {
      parts.push(`${count} ${count === 1 ? unit.singular : unit.plural}`);
      remaining -= count * unit.seconds;
    }
  }

  const result = parts.join(', ');
  return !isEmptyString(result) ? result : '0 seconds';
};

/**
 * Format a number of seconds to a human-readable duration string.
 * Uses the single most appropriate unit for clarity (e.g., "8 days" not "1 week 1 day").
 */
export const formatSecondsToHumanReadable = (seconds: number): string => {
  if (seconds === 0) return '0 seconds';
  if (seconds < 0) return `${formatSecondsToHumanReadable(Math.abs(seconds))} (negative)`;

  const MINUTE = 60;
  const HOUR = MINUTE * 60;
  const DAY = HOUR * 24;

  const withUnit = (count: number, word: string) => `${count} ${plur(word, count)}`;

  if (seconds >= DAY) {
    const totalDays = Math.floor(seconds / DAY);
    const remainingHours = Math.floor((seconds % DAY) / HOUR);
    if (remainingHours === 0) return withUnit(totalDays, 'day');
    return `${withUnit(totalDays, 'day')} ${withUnit(remainingHours, 'hour')}`;
  }

  if (seconds >= HOUR) {
    const totalHours = Math.floor(seconds / HOUR);
    const remainingMinutes = Math.floor((seconds % HOUR) / MINUTE);
    if (remainingMinutes === 0) return withUnit(totalHours, 'hour');
    return `${withUnit(totalHours, 'hour')} ${withUnit(remainingMinutes, 'minute')}`;
  }

  if (seconds >= MINUTE) {
    const totalMinutes = Math.floor(seconds / MINUTE);
    const remainingSeconds = seconds % MINUTE;
    if (remainingSeconds === 0) return withUnit(totalMinutes, 'minute');
    return `${withUnit(totalMinutes, 'minute')} ${withUnit(remainingSeconds, 'second')}`;
  }

  return withUnit(seconds, 'second');
};
